import { Outlet, Link } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useCurrentUser, useLogout } from '../lib/api/queries';

/**
 * Layout shell for the client-user experience at <c>/client/*</c>.
 * Reduced chrome — no product nav, no admin, no tenant/version chips.
 * Client users only see reports for their organisation and a sign-out
 * control; anything else would be distracting or reveal DPAS-internal
 * navigation they don't have permission to reach.
 *
 * Wraps a Router {@link Outlet} so the client routes render inside it.
 */
export function ClientLayout() {
  const { data: user } = useCurrentUser();
  const logout = useLogout();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="bg-accent-600 text-white shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src="/dpas-diamond-logo.png"
              alt="DPAS"
              className="w-9 h-9 object-contain"
            />
            <Link to="/client/reports"
                  className="text-xl font-semibold tracking-tight">
              DPAS Reports
            </Link>
          </div>
          <div className="flex items-center gap-3 text-sm">
            {user && (
              <span className="text-accent-100" title={user.email}>
                {user.email}
              </span>
            )}
            <button
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md
                         bg-accent-700/40 hover:bg-accent-700/70 transition-colors
                         disabled:opacity-50"
              title="Sign out"
            >
              <LogOut className="w-3.5 h-3.5" aria-hidden />
              Sign out
            </button>
          </div>
        </div>
        {/* Same three-band strip as the staff shell — brand consistency
            without giving away the staff nav. */}
        <div className="h-1 flex">
          <div className="flex-1 bg-brand-500" />
          <div className="flex-1 bg-accent-500" />
          <div className="flex-1 bg-accent-alt-500" />
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-8">
        <Outlet />
      </main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-3 text-xs text-gray-500 text-center">
          Confidential — for internal use only.
        </div>
      </footer>
    </div>
  );
}
